import { useEffect, useState, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Users, Crown, Sparkles, Star } from "lucide-react";

const STORAGE_KEY = "nebula_community_stats_v2";

interface Stats {
  explorers: number;
  vip: number;
  cosmic: number;
  readings: number;
  lastUpdated: number;
}

const INITIAL: Stats = { explorers: 142, vip: 18, cosmic: 6, readings: 2340, lastUpdated: Date.now() };

function loadStats(): Stats {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.explorers && parsed.lastUpdated) return parsed;
    }
  } catch {}
  return { ...INITIAL };
}

function rand(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function applyCatchUp(stored: Stats): { stats: Stats; events: EventItem[] } {
  const now = Date.now();
  const elapsedMin = Math.max(0, now - stored.lastUpdated) / 60000;
  const events: EventItem[] = [];

  if (elapsedMin < 0.1) return { stats: { ...stored, lastUpdated: now }, events };

  const explorerGrowth = Math.floor(elapsedMin * rand(1, 3));
  const vipGrowth = Math.floor(elapsedMin / rand(15, 25));
  const cosmicGrowth = Math.floor(elapsedMin / rand(30, 45));
  const readingsGrowth = Math.floor(elapsedMin * rand(5, 15));

  if (explorerGrowth > 0) events.push({ text: `+${explorerGrowth} Explorers joined`, icon: "explorers" });
  if (vipGrowth > 0) events.push({ text: `${vipGrowth} upgraded to VIP`, icon: "vip" });
  if (cosmicGrowth > 0) events.push({ text: `${cosmicGrowth} unlocked Cosmic Tier!`, icon: "cosmic" });
  if (readingsGrowth > 0) events.push({ text: `${readingsGrowth} new readings generated`, icon: "readings" });

  return {
    stats: {
      explorers: stored.explorers + Math.max(0, explorerGrowth),
      vip: stored.vip + Math.max(0, vipGrowth),
      cosmic: stored.cosmic + Math.max(0, cosmicGrowth),
      readings: stored.readings + Math.max(0, readingsGrowth),
      lastUpdated: now,
    },
    events,
  };
}

function saveStats(stats: Stats) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(stats)); } catch {}
}

function useAnimatedCounter(target: number, duration = 1200) {
  const [value, setValue] = useState(target);
  const prevTarget = useRef(target);

  useEffect(() => {
    const from = prevTarget.current;
    prevTarget.current = target;
    if (from === target) { setValue(target); return; }
    const start = performance.now();
    let raf: number;
    const animate = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(from + (target - from) * eased));
      if (progress < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  return value;
}

export interface EventItem {
  text: string;
  icon: "explorers" | "vip" | "cosmic" | "readings";
}

interface CommunityStatsProps {
  onEvent?: (events: EventItem[]) => void;
}

const CommunityStats = ({ onEvent }: CommunityStatsProps) => {
  const [stats, setStats] = useState<Stats>(() => {
    const { stats: caught, events } = applyCatchUp(loadStats());
    saveStats(caught);
    // defer event dispatch
    if (events.length > 0) setTimeout(() => onEvent?.(events), 100);
    return caught;
  });

  const tick = useCallback(() => {
    setStats((prev) => {
      const newEvents: EventItem[] = [];
      const explorerInc = rand(1, 2);
      const vipInc = Math.random() < 0.08 ? 1 : 0;
      const cosmicInc = Math.random() < 0.04 ? 1 : 0;
      const readingsInc = rand(5, 10);

      if (explorerInc > 0) newEvents.push({ text: `+${explorerInc} Explorers joined`, icon: "explorers" });
      if (vipInc > 0) newEvents.push({ text: "Someone upgraded to VIP", icon: "vip" });
      if (cosmicInc > 0) newEvents.push({ text: "Cosmic Tier unlocked!", icon: "cosmic" });
      if (readingsInc > 0) newEvents.push({ text: `${readingsInc} new readings generated`, icon: "readings" });

      const next: Stats = {
        explorers: prev.explorers + explorerInc,
        vip: prev.vip + vipInc,
        cosmic: prev.cosmic + cosmicInc,
        readings: prev.readings + readingsInc,
        lastUpdated: Date.now(),
      };
      saveStats(next);
      if (newEvents.length > 0) setTimeout(() => onEvent?.(newEvents), 0);
      return next;
    });
  }, [onEvent]);

  useEffect(() => {
    const interval = setInterval(tick, rand(8000, 12000));
    return () => clearInterval(interval);
  }, [tick]);

  const items = [
    { icon: Users, label: "Explorers", value: stats.explorers, color: "text-primary" },
    { icon: Crown, label: "VIP Members", value: stats.vip, color: "text-cosmic-gold" },
    { icon: Star, label: "Cosmic Tier", value: stats.cosmic, color: "text-purple-400" },
    { icon: Sparkles, label: "Readings Generated", value: stats.readings, color: "text-cosmic-teal" },
  ];

  return (
    <section className="relative z-10 py-16">
      <div className="container mx-auto px-4">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-display text-2xl md:text-3xl font-bold text-center cosmic-text-glow mb-10"
        >
          Nebula Community
        </motion.h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
          {items.map((item, i) => (
            <StatCard key={item.label} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

const StatCard = ({ item, index }: { item: { icon: any; label: string; value: number; color: string }; index: number }) => {
  const animated = useAnimatedCounter(item.value);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.12 }}
      className="rounded-2xl p-5 text-center backdrop-blur-xl bg-card/40 border border-primary/10"
      style={{ boxShadow: "0 0 30px hsl(270 60% 60% / 0.1), inset 0 1px 0 hsl(0 0% 100% / 0.05)" }}
    >
      <item.icon className={`w-7 h-7 mx-auto mb-2 ${item.color}`} />
      <p className="font-display text-2xl md:text-3xl font-bold">{animated.toLocaleString()}</p>
      <p className="text-xs text-muted-foreground mt-1">{item.label}</p>
    </motion.div>
  );
};

export default CommunityStats;
